import { toast } from 'react-toastify';
import { ContactUs, ContactUsResponse } from '@core/models/contact.model';
import { IErrorModel } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ContactService from 'services/api/contact.service';

// -------------------------------------------------------------------------

interface IContactState {
    loading: boolean;
    error: IErrorModel | undefined;
    contacts: ContactUs[];
    pagination: {
        pageNumber: number;
        pageSize: number;
        totalPages: number;
        totalElements: number;
    };
    getAllContactsLoading: boolean;
}

const initialState: IContactState = {
    loading: false,
    error: {} as IErrorModel,
    contacts: [],
    pagination: {
        pageNumber: 0,
        pageSize: 20,
        totalPages: 0,
        totalElements: 0,
    },
    getAllContactsLoading: false,
};

// -------------------------------------------------------------------------

export const getAllContactsThunk = createAsyncThunk<
    ContactUsResponse,
    { pageNumber: number; pageSize: number },
    { rejectValue: IErrorModel }
>(`/contact/getAllContacts`, async ({ pageNumber, pageSize }, { rejectWithValue }: any) => {
    try {
        const response: any = await ContactService.getAllContacts(pageNumber, pageSize);
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch contacts', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

// -------------------------------------------------------------------------

const contactSlice = createSlice({
    name: `contactSlice`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getAllContactsThunk.pending, (state) => {
            state.getAllContactsLoading = true;
            state.loading = true;
        });
        builder.addCase(getAllContactsThunk.fulfilled, (state, action) => {
            state.getAllContactsLoading = false;
            state.loading = false;
            state.contacts = action.payload.content || [];
            state.pagination = {
                pageNumber: action.payload.pageable?.pageNumber || 0,
                pageSize: action.payload.pageable?.pageSize || 20,
                totalPages: action.payload.totalPages || 0,
                totalElements: action.payload.totalElements || 0,
            };
        });
        builder.addCase(getAllContactsThunk.rejected, (state, action) => {
            state.getAllContactsLoading = false;
            state.loading = false;
            state.error = action.payload;
        });
    },
});

export default contactSlice;
export const contactReducer = contactSlice.reducer;

